import { ApolloError } from "@apollo/client";
import { Alert, Box, ButtonBase, Link, Paper, Tooltip, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { memberName, ReportMemberLike } from "./reportFormat";

/** A headline number: the label, the value, and one line of context under it. */
export const StatTile: React.FC<{
  label: string;
  value: string;
  caption?: string;
  onClick?: () => void;
  /** Smaller for a value that is a name rather than a number. */
  valueSize?: number;
}> = ({ label, value, caption, onClick, valueSize = 28 }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={(event) => onClick && (event.key === "Enter" || event.key === " ") && onClick()}
    sx={{
      p: 2,
      minWidth: 150,
      flex: "1 1 150px",
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { borderColor: "primary.main" } : undefined,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: valueSize,
        fontWeight: 600,
        // A smaller value sits on the same baseline band as a full-size number in the next tile.
        lineHeight: valueSize < 28 ? `${28 * 1.3}px` : 1.3,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
  </Paper>
);

export const TileRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>{children}</Box>
);

export const Section: React.FC<{ title: string; note?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  note,
  children,
}) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Typography variant="h6">{title}</Typography>
    {note && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        {note}
      </Typography>
    )}
    {children}
  </Paper>
);

/** Sequential slot of the reference palette: one hue, for a magnitude. */
export const BAR = "#2a78d6";

export interface BarListRow {
  key: string;
  label: string;
  value: number;
  /** What the hover adds to the label and the number. */
  detail?: string;
}

/**
 * A ranked list as bars, the longest full width. Each row is a button when
 * `onPick` is set. The number is always beside its bar, so nothing depends on
 * hovering, and the report's table view lists the same figures.
 */
export const BarList: React.FC<{
  rows: BarListRow[];
  title: string;
  selected?: string | null;
  onPick?: (key: string) => void;
}> = ({ rows, title, selected, onPick }) => {
  const most = Math.max(1, ...rows.map((row) => row.value));
  return (
    <Box role="list" aria-label={title} sx={{ display: "grid", gap: 0.5 }}>
      {rows.map((row) => (
        <Tooltip key={row.key} title={row.detail ? `${row.label}: ${row.detail}` : ""} placement="top" describeChild>
          <ButtonBase
            role="listitem"
            component={onPick ? "button" : "div"}
            disabled={!onPick}
            onClick={onPick ? () => onPick(row.key) : undefined}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr) auto", sm: "200px minmax(0, 1fr) 64px" },
              columnGap: 1.5,
              alignItems: "center",
              width: "100%",
              px: 1,
              py: 0.75,
              borderRadius: 1,
              textAlign: "left",
              font: "inherit",
              "&.Mui-disabled": { pointerEvents: "auto", color: "inherit" },
              bgcolor: selected === row.key ? "action.selected" : undefined,
              "&:hover": { bgcolor: onPick ? "action.hover" : undefined },
            }}
          >
            <Typography variant="body2" noWrap>
              {row.label}
            </Typography>
            <Box
              sx={{
                gridRow: { xs: 2, sm: 1 },
                gridColumn: { xs: "1 / -1", sm: 2 },
                mt: { xs: 0.5, sm: 0 },
                height: 12,
                bgcolor: "action.hover",
                borderRadius: "0 4px 4px 0",
              }}
            >
              {/* No bar at all for zero: even a sliver would read as "a few". */}
              {row.value > 0 && (
                <Box sx={{ width: `${Math.max(1, (row.value / most) * 100)}%`, height: "100%", bgcolor: BAR, borderRadius: "0 4px 4px 0" }} />
              )}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {row.value.toLocaleString("en-US")}
            </Typography>
          </ButtonBase>
        </Tooltip>
      ))}
    </Box>
  );
};

/** A member's name, linking to their page on the dashboard. */
export const MemberLink: React.FC<{ member: (ReportMemberLike & { id: number }) | null | undefined }> = ({ member }) =>
  member ? (
    <Link component={RouterLink} to={`/users/${member.id}`} underline="hover">
      {memberName(member)}
    </Link>
  ) : null;

/**
 * A failed report. Rate limits are announced app-wide by the Apollo link, so
 * they are not repeated here; anything else shows its message, which for a bad
 * range is the server's own sentence.
 */
export const ReportError: React.FC<{ error?: ApolloError }> = ({ error }) => {
  if (!error) return null;
  const limited = error.graphQLErrors?.some((item) => item.extensions?.code === "TOO_MANY_REQUESTS");
  return (
    <Alert severity={limited ? "warning" : "error"}>
      {limited ? "Too many reports at once. Wait a moment, then change a filter to try again." : error.message}
    </Alert>
  );
};
